import React, { useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { xml } from '@codemirror/lang-xml';
import { html } from '@codemirror/lang-html';
import {
    search,
    searchKeymap,
    highlightSelectionMatches
} from '@codemirror/search';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { foldCode, codeFolding, foldEffect, unfoldEffect } from '@codemirror/language';
import { syntaxTree } from '@codemirror/language';
import { oneDarkTheme } from './editor/theme';
import {
    calculateFoldRange,
    collectFoldableNodes,
    createFoldPlaceholder,
    createFoldPlaceholderDataGetter,
    findBracketPosition,
} from './editor/folding';
import { FastSearchPanel, searchPhrases, toggleSearchPanel } from './editor/searchPanel';

const clickToFold = EditorView.domEventHandlers({
    mousedown: (event, view) => {
        // Get the clicked position
        const pos = view.posAtDOM(event.target);
        if (pos === null) return;

        // Check if the click is on a bracket/brace
        const line = view.state.doc.lineAt(pos);
        const char = line.text[pos - line.from];

        // Simple check for start brackets - robust check would involve syntax tree
        if (char === '{' || char === '[') {
            // Attempt to toggle fold at this position
            foldCode(view, { code: { from: pos, to: pos } });
            // We don't preventDefault to allow cursor placement, 
            // but if fold happens, visual change is enough.
        }
    }
});

const SmartEditor = forwardRef(({ value, onChange, language = 'json', readOnly = false, uiLanguage = 'en' }, ref) => {
    const editorRef = React.useRef(null);
    const viewRef = React.useRef(null);
    const containerRef = React.useRef(null);

    useEffect(() => {
        const handleGlobalKeydown = (event) => {
            const isFindShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f';
            if (!isFindShortcut || event.altKey) {
                return;
            }

            const view = viewRef.current || editorRef.current?.view;
            if (!view) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            toggleSearchPanel(view);
        };

        window.addEventListener('keydown', handleGlobalKeydown, true);
        return () => window.removeEventListener('keydown', handleGlobalKeydown, true);
    }, []);

    useImperativeHandle(ref, () => ({
        foldAll: () => {
            const view = viewRef.current || editorRef.current?.view;
            if (!view) {
                console.warn('Editor view not available');
                return;
            }

            try {
                const tree = syntaxTree(view.state);
                const nodes = collectFoldableNodes(tree);
                
                if (nodes.length === 0) return;
                
                // 从后往前折叠，避免位置偏移（先折叠内层节点，再折叠外层节点）
                nodes.sort((a, b) => b.from - a.from);
                
                // 逐个应用折叠效果，确保每次使用最新状态
                nodes.forEach(({ from, to }) => {
                    try {
                        const startLine = view.state.doc.lineAt(from);
                        const endLine = view.state.doc.lineAt(to);
                        
                        // 只有跨越多行的节点才能折叠
                        if (endLine.number <= startLine.number) return;
                        
                        const bracketPos = findBracketPosition(view.state.doc, from, to);
                        const foldRange = foldCode(view, { code: { from: bracketPos, to: bracketPos } });
                        
                        if (foldRange?.from !== undefined && foldRange?.to !== undefined) {
                            view.dispatch({ effects: [foldEffect.of(foldRange)] });
                        } else {
                            // 使用备选方案：计算折叠范围
                            const calculatedRange = calculateFoldRange(startLine, endLine);
                            if (calculatedRange) {
                                view.dispatch({ effects: [foldEffect.of(calculatedRange)] });
                            }
                        }
                    } catch (e) {
                        // 静默处理错误，避免影响其他节点的折叠
                    }
                });
            } catch (error) {
                console.error('Error in foldAll:', error);
            }
        },
        unfoldAll: () => {
            const view = viewRef.current || editorRef.current?.view;
            if (!view) {
                console.warn('Editor view not available');
                return;
            }

            try {
                const tree = syntaxTree(view.state);
                const nodes = collectFoldableNodes(tree);
                
                if (nodes.length === 0) return;
                
                // 从内到外展开（从小到大），先展开子节点
                nodes.sort((a, b) => a.size - b.size);
                
                // 逐个应用展开效果，使用与 foldAll 相同的逻辑
                nodes.forEach(({ from, to }) => {
                    try {
                        const startLine = view.state.doc.lineAt(from);
                        const endLine = view.state.doc.lineAt(to);
                        
                        // 只有跨越多行的节点才能展开
                        if (endLine.number <= startLine.number) return;
                        
                        const bracketPos = findBracketPosition(view.state.doc, from, to);
                        const foldRange = foldCode(view, { code: { from: bracketPos, to: bracketPos } });
                        
                        if (foldRange?.from !== undefined && foldRange?.to !== undefined) {
                            view.dispatch({ effects: [unfoldEffect.of({ from: foldRange.from, to: foldRange.to })] });
                        } else {
                            // 使用备选方案：计算折叠范围
                            const calculatedRange = calculateFoldRange(startLine, endLine);
                            if (calculatedRange) {
                                view.dispatch({ effects: [unfoldEffect.of(calculatedRange)] });
                            }
                        }
                    } catch (e) {
                        // 静默处理错误，避免影响其他节点的展开
                    }
                });
            } catch (error) {
                console.error('Error in unfoldAll:', error);
            }
        }
    }));

    const extensions = useMemo(() => {
        const phrases = searchPhrases[uiLanguage] || searchPhrases.en;
        const getFoldPlaceholderData = createFoldPlaceholderDataGetter(uiLanguage);
        // 始终启用自动换行，包括压缩后的内容
        const baseExtensions = [
            EditorState.phrases.of(phrases),
            codeFolding({
                placeholderDOM: createFoldPlaceholder,
                preparePlaceholder: getFoldPlaceholderData,
                placeholderText: '...'
            }),
            search({
                top: true,
                createPanel: (view) => new FastSearchPanel(view, phrases)
            }),
            highlightSelectionMatches(),
            keymap.of([{
                key: 'Mod-f',
                run: toggleSearchPanel,
                preventDefault: true,
                scope: 'editor search-panel'
            }]),
            keymap.of(searchKeymap),
            EditorView.lineWrapping,
            clickToFold
        ];
        switch (language) {
            case 'json': return [json(), ...baseExtensions];
            case 'xml': return [xml(), ...baseExtensions];
            case 'html': return [html(), ...baseExtensions];
            default: return baseExtensions;
        }
    }, [language, uiLanguage]);

    return (
        <div
            ref={containerRef}
            style={{ 
            height: '100%', 
            borderRadius: '0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <CodeMirror
                ref={(editor) => {
                    editorRef.current = editor;
                    if (editor?.view) {
                        viewRef.current = editor.view;
                    }
                }}
                value={value}
                height="100%"
                theme={oneDarkTheme}
                extensions={extensions}
                onChange={onChange}
                readOnly={readOnly}
                basicSetup={{
                    foldGutter: true,
                    dropCursor: false,
                    allowMultipleSelections: false,
                    indentOnInput: false,
                    lineNumbers: true,
                    highlightActiveLineGutter: true,
                    highlightActiveLine: true,
                    bracketMatching: true,
                    closeBrackets: true,
                    autocompletion: true,
                    history: true,
                }}
                style={{ 
                    fontSize: '14px', 
                    fontFamily: '"JetBrains Mono", monospace', 
                    height: '100%',
                    padding: '0'
                }}
            />
        </div>
    );
});

SmartEditor.displayName = 'SmartEditor';

export default SmartEditor;
